import type { ReactElement } from 'react';
import {
  AuthProvider,
  DevPanel,
  LoginPage,
  RegisterPage,
  TesterPanel,
  TesterPanelProvider,
  resolveApiUrl,
  resolveAppUrl,
} from '@fetmode/app-core';
import { LocaleProvider, useLocale } from '@fetmode/i18n';
import { defaultTheme, ThemeProvider } from '@fetmode/theme';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';

import { ApplicationStepsPage } from './features/onboarding/ApplicationStepsPage.js';
import { StudioHomePage } from './features/onboarding/index.js';
import { DashboardPage } from './features/dashboard/index.js';
import { ContentPage } from './features/content/index.js';
import { LiveControlsPage } from './features/live/index.js';
import { RoxyPage, RoxyProvider } from './features/roxy/studio-roxy.js';
import { WalletPage } from './features/wallet/index.js';
import {
  SettingsAnalyticsPage,
  SettingsCompliancePage,
  SettingsDiscountsPage,
  SettingsFansPage,
  SettingsLayout,
  SettingsPayoutsPage,
  SettingsPricingPage,
  SettingsProfilePage,
  SettingsRequestsPage,
  SettingsRoomPage,
  SettingsSafetyPage,
  SettingsSchedulePage,
} from './features/settings/index.js';
import { StreamSessionGuard, StudioModelProvider, ApplicationGate } from './features/studio/index.js';
import { StudioProtectedRoute } from './shared/auth/StudioProtectedRoute.js';
import { AppShell } from './shared/components/AppShell/index.js';

const apiBaseUrl = resolveApiUrl(import.meta.env);

function StudioAcceptInviteRedirect(): ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const consumerBase = resolveAppUrl('consumer', import.meta.env as Record<string, string | undefined>);
  const target = token
    ? `${consumerBase}/accept-invite?token=${encodeURIComponent(token)}`
    : `${consumerBase}/accept-invite`;
  window.location.replace(target);
  return <p style={{ padding: '1.5rem' }}>Redirecting to accept your invite…</p>;
}

function AppRoutes(): ReactElement {
  useLocale();

  return (
    <StudioModelProvider>
      <RoxyProvider>
        <AppShell>
          <Routes>
          <Route path="/" element={<StudioHomePage />} />
          <Route path="/become" element={<Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Navigate to="/settings/overview" replace />} />
          <Route
            path="/settings"
            element={
              <StudioProtectedRoute>
                <ApplicationGate>
                  <StreamSessionGuard>
                    <SettingsLayout />
                  </StreamSessionGuard>
                </ApplicationGate>
              </StudioProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<DashboardPage />} />
            <Route path="analytics" element={<SettingsAnalyticsPage />} />
            <Route path="profile" element={<SettingsProfilePage />} />
            <Route path="pricing" element={<SettingsPricingPage />} />
            <Route path="discounts" element={<SettingsDiscountsPage />} />
            <Route path="requests" element={<SettingsRequestsPage />} />
            <Route path="room" element={<SettingsRoomPage />} />
            <Route path="fans" element={<SettingsFansPage />} />
            <Route path="schedule" element={<SettingsSchedulePage />} />
            <Route path="safety" element={<SettingsSafetyPage />} />
            <Route path="compliance" element={<SettingsCompliancePage />} />
            <Route path="payouts" element={<SettingsPayoutsPage />} />
            <Route path="voice" element={<Navigate to="/roxy" replace />} />
          </Route>
          <Route
            path="/roxy"
            element={
              <StudioProtectedRoute>
                <ApplicationGate>
                  <StreamSessionGuard>
                    <RoxyPage />
                  </StreamSessionGuard>
                </ApplicationGate>
              </StudioProtectedRoute>
            }
          />
          <Route
            path="/live"
            element={
              <StudioProtectedRoute>
                <ApplicationGate>
                  <LiveControlsPage />
                </ApplicationGate>
              </StudioProtectedRoute>
            }
          />
          <Route
            path="/content"
            element={
              <StudioProtectedRoute>
                <ApplicationGate>
                  <StreamSessionGuard>
                    <ContentPage />
                  </StreamSessionGuard>
                </ApplicationGate>
              </StudioProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <StudioProtectedRoute>
                <ApplicationGate>
                  <StreamSessionGuard>
                    <WalletPage />
                  </StreamSessionGuard>
                </ApplicationGate>
              </StudioProtectedRoute>
            }
          />
          <Route path="/earnings" element={<Navigate to="/settings/analytics" replace />} />
          <Route
            path="/onboarding"
            element={
              <StudioProtectedRoute>
                <ApplicationStepsPage />
              </StudioProtectedRoute>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage afterRegisterPath="/onboarding" collectLegalName />} />
          <Route path="/accept-invite" element={<StudioAcceptInviteRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppShell>
      </RoxyProvider>
    </StudioModelProvider>
  );
}

export function App(): ReactElement {
  return (
    <ThemeProvider theme={defaultTheme}>
      <BrowserRouter>
        <AuthProvider apiBaseUrl={apiBaseUrl}>
          <LocaleProvider>
            <TesterPanelProvider>
              <AppRoutes />
              <TesterPanel variant="studio" />
              {import.meta.env.DEV ? <DevPanel variant="studio" /> : null}
            </TesterPanelProvider>
          </LocaleProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
